<template>
  <tab-item-template>
    <el-table
        :data="dataPropertyImplementationView"
        border>
      <el-table-column
          width="56"
          align="center"
          prop="index"
          label="追番">
      </el-table-column>

      <el-table-column
          min-width="35"
          prop="name"
          label="名称">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name"
                :value-id="row.id">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="35"
          prop="description"
          label="説明">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="35"
          prop="localId"
          label="内部ID">
        <template #default="{row}">
          <table-inline>
            <local-id-field
                :value="row.localId">
            </local-id-field>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="120"
          align="center"
          prop="dataType"
          label="データ型">
      </el-table-column>

      <el-table-column
          width="80"
          align="center"
          prop="isPrimaryKey"
          label="主キー">
        <template #default="{row}">
          <readonly-checkbox :value="row.isPrimaryKey"></readonly-checkbox>
        </template>
      </el-table-column>

      <el-table-column
          width="80"
          align="center"
          prop="isRequired"
          label="必須">
        <template #default="{row}">
          <readonly-checkbox :value="row.isRequired"></readonly-checkbox>
        </template>
      </el-table-column>

      <el-table-column
          v-if="isEditable"
          width="60"
          align="right"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                type="edit-content"
                @click="openUpdateDialog(row)">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <!-- データ項目実装編集ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate.visible"
        title-text="データ項目実装編集"
        @close="closeUpdateDialog">
      <data-property-implementation-form
          v-model="dialogUpdate.form">
      </data-property-implementation-form>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import BetterSet from "betterset";
import gql from "graphql-tag";
import {mapActions, mapGetters, mapMutations} from "vuex";
import {DATA_PROPERTY_DEFINITION_DATA_TYPES} from "@/constants";
import TableInline from "@/components/atoms/TableInline.vue";
import LocalIdField from "@/components/molecules/LocalIdField.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import DataPropertyImplementationForm
  from "@/components/pages/implementation/DataImplementationShow/DataPropertyImplementationForm";
import PathInfoMixin from "@/router/PathInfoMixin";
import Formatter from "@/utils/Formatter";

export default {
  name: 'DataPropertyImplementationTable',
  mixins: [
    PathInfoMixin,
    Formatter
  ],
  components: {
    DataPropertyImplementationForm,
    SimpleDialog,
    IconBtn,
    IconBtns,
    TextFieldLike,
    LocalIdField,
    TableInline,
    ReadonlyCheckbox,
    TabItemTemplate
  },
  props: {
    dataPropertyImplementations: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      dialogUpdate: {
        visible: false,
        form: null
      },
    };
  },
  computed: {
    ...mapGetters({
      hdsEntities: 'hds/entities'
    }),
    dataPropertyImplementationView() {
      return this.dataPropertyImplementations.map(dpi => {
        const dpd = this.hdsEntities.dataPropertyDefinition.findOne({id: dpi.dataPropertyDefinitionId});
        return {
          ...dpi,
          name: dpd?.name,
          description: dpd?.description,
          dataType: DATA_PROPERTY_DEFINITION_DATA_TYPES[dpd?.dataType] || '',
          isPrimaryKey: dpd?.isPrimaryKey,
          isRequired: dpd?.isRequired
        };
      });
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad',
    }),
    openUpdateDialog(dataPropertyImplementation) {
      this.dialogUpdate = {
        visible: true,
        form: dataPropertyImplementation
      };
    },
    async closeUpdateDialog(apply) {
      if(apply) {
        this.showSnackbar({
          message: 'データ項目実装を更新中...',
          timeout: false,
          loading: true
        });
        try {
          await this.$store.dispatch('hcs/dataPropertyImplementation/update', this.dialogUpdate.form);
          this.dialogUpdate.visible = false;
          this.showSnackbar({
            message: `データ項目実装が更新されました`
          });
        } catch (e) {
          console.error(e);
          this.showSnackbar({
            color: 'failed',
            message: this.formatError(e, 'データ項目実装の更新に失敗しました')
          });
        }
      } else {
        this.dialogUpdate.visible = false;
      }
    },
  },
  watch: {
    dataPropertyImplementations: {
      immediate: true,
      handler(dataPropertyImplementations) {
        // データ項目実装に使用されているデータ項目定義を取得
        const dataPropertyDefinitionIds = dataPropertyImplementations
            .map(dpi => dpi.dataPropertyDefinitionId)
            .reduce((set, value) => {set.add(value); return set;}, new BetterSet())
            .asArray();

        this.gqlQueryAndLoad({
          query: gql`
            query FindDataPropertyDefinitions(
                $dataPropertyDefinitionIds: [ID!]!,
            ) {
                dataPropertyDefinition: dataPropertyDefinitionInBulk(dataPropertyDefinitionIds: $dataPropertyDefinitionIds) {
                    id
                    name
                    description
                    dataType
                    isPrimaryKey
                    isRequired
                }
            }`,
          variables: {
            dataPropertyDefinitionIds
          },
          mapping: {
            dataPropertyDefinition: 'dataPropertyDefinition*'
          }
        });
      }
    }
  }
}
</script>
